import React, { useEffect } from 'react';
import { Button, Input, Select, Space, Table, Tag, Tooltip, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useEmployees } from '../../hooks/useEmployees';
import { useDepartments } from '../../hooks/useDepartments';
import { Employee } from '../../types';

const { Option } = Select;

const statusColor: Record<string, string> = {
  ACTIVE: 'green', INACTIVE: 'default', ON_LEAVE: 'orange', TERMINATED: 'red',
};

const EmployeeListView: React.FC = () => {
  const navigate = useNavigate();
  const {
    employees, totalEmployees, isLoading, currentPage, pageSize,
    fetchEmployees, deleteEmployee, setSearchTerm, setStatusFilter, setDepartmentFilter,
    searchTerm, statusFilter, departmentFilter,
  } = useEmployees();
  const { departments, fetchDepartments } = useDepartments();

  useEffect(() => {
    fetchDepartments();
    fetchEmployees(0);
  }, []);

  const handleSearch = () => fetchEmployees(0);

  const columns = [
    { title: 'Emp No.', dataIndex: 'employeeNumber', key: 'employeeNumber', width: 110 },
    {
      title: 'Name',
      key: 'name',
      render: (_: any, r: Employee) => `${r.firstName}${r.middleName ? ' ' + r.middleName : ''} ${r.lastName}`,
    },
    { title: 'Department', dataIndex: 'departmentName', key: 'departmentName' },
    { title: 'Position', dataIndex: 'positionTitle', key: 'positionTitle' },
    { title: 'Type', dataIndex: 'employmentType', key: 'employmentType' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => <Tag color={statusColor[s] ?? 'default'}>{s}</Tag>,
    },
    { title: 'Hire Date', dataIndex: 'hireDate', key: 'hireDate', width: 110 },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: Employee) => (
        <Space size="small">
          <Tooltip title="View">
            <Button icon={<EyeOutlined />} size="small" onClick={() => navigate(`/hr/employees/${record.id}`)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button icon={<EditOutlined />} size="small" onClick={() => navigate(`/hr/employees/${record.id}/edit`)} />
          </Tooltip>
          <Popconfirm title="Delete this employee?" onConfirm={() => deleteEmployee(record.id)}>
            <Tooltip title="Delete">
              <Button icon={<DeleteOutlined />} size="small" danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex-between mb-4">
        <Space wrap>
          <Input
            placeholder="Search employees..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 220 }}
          />
          <Select
            placeholder="Status"
            value={statusFilter || undefined}
            onChange={setStatusFilter}
            allowClear
            style={{ width: 140 }}
          >
            <Option value="ACTIVE">Active</Option>
            <Option value="INACTIVE">Inactive</Option>
            <Option value="ON_LEAVE">On Leave</Option>
            <Option value="TERMINATED">Terminated</Option>
          </Select>
          <Select
            placeholder="Department"
            value={departmentFilter ?? undefined}
            onChange={setDepartmentFilter}
            allowClear
            showSearch
            optionFilterProp="children"
            style={{ width: 180 }}
          >
            {departments.map((d) => <Option key={d.id} value={d.id}>{d.name}</Option>)}
          </Select>
          <Button icon={<SearchOutlined />} onClick={handleSearch}>Search</Button>
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/employees/create')}>
          New Employee
        </Button>
      </div>

      <Table
        dataSource={employees}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={{
          current: currentPage + 1,
          pageSize,
          total: totalEmployees,
          showSizeChanger: false,
          showTotal: (t) => `${t} employees`,
          onChange: (page) => fetchEmployees(page - 1),
        }}
        size="small"
      />
    </div>
  );
};

export default EmployeeListView;
